import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReactComponent as Burguer } from '../../../assets/images/burguer.svg';
import DrawerChangeLanguageMobile from '../../DrawerChangeLanguageMobile';
import Login from '../../../components/Login';
import { useMemorial } from '../../../context/memorial.provider';
import { logout } from '../../../context/memorial.actions';
import { useIsMobile } from '../../../utils/hooks';
import { Dropdown, MenuProps } from 'antd';
import AvatarMemorial from '../../Avatar';
import { ReactComponent as Logo } from '../../../assets/images/logoBlueNegative.svg';
import { goToWPHome } from '../../../utils';

const Menu = ({
  setShowLoginModalAutomatically,
  showLoginModalAutomatically,
}: {
  setShowLoginModalAutomatically?: (value: boolean) => void;
  showLoginModalAutomatically?: boolean;
}) => {
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const [showDrawerChangeLanguageMobile, setShowDrawerChangeLanguageMobile] =
    useState<boolean>(false);
  const [showModalLogin, setShowModalLogin] = useState<boolean>(false);
  const { firstName, hasLockedTheApp, id, image, lastName, memorialDispatch, roles } = useMemorial();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const getItemsToDropdown = (): MenuProps['items'] => {
    const items: {
      disabled?: boolean;
      label: string;
      onClick?: () => void;
      to?: string;
      type: 'item' | 'group';
    }[] = [
      { label: t('topbar.menuTitle'), type: 'group' },
      ...(id ? [] : [{ label: t('common.start'), to: '/', type: 'item' as 'item' }]),
      { label: t('common.home'), onClick: goToWPHome, type: 'item' },
      {
        disabled: hasLockedTheApp,
        label: t('start.exploreMemorials'),
        to: '/home/start?page=1&pageSize=9',
        type: 'item',
      },
    ];

    if (id) {
      items.push(
        {
          disabled: hasLockedTheApp,
          label: t('topbar.myMemorials'),
          to: '/home/memorials/list?page=1',
          type: 'item',
        },
        {
          disabled: hasLockedTheApp,
          label: t('topbar.commentsManagement'),
          to: '/home/memorials/comments-management',
          type: 'item',
        },
        {
          disabled: hasLockedTheApp,
          label: t('topbar.facebookProfileManagement'),
          to: '/home/facebook-profile',
          type: 'item',
        },
        {
          label: t('common.myAccount'),
          to: '/home/account',
          type: 'item',
        }
      );

      if (roles?.includes('ROLE_ADMIN')) {
        items.push({
          label: 'Backoffice',
          onClick: () => {
            const token = localStorage.getItem('memorial_token');
            window.open(
              `${process.env.REACT_APP_API_URL}/easy-admin-bridge?token=${token}`,
              '_blank'
            );
          },
          type: 'item',
        });
      }

      if (isMobile) {
        items.push({
          label: t('common.changeLanguage'),
          onClick: () => setShowDrawerChangeLanguageMobile(true),
          type: 'item',
        });
      }

      items.push({
        label: t('topbar.logout'),
        onClick: () => {
          localStorage.removeItem('memorial_token');
          memorialDispatch(logout());
          navigate('/');
        },
        type: 'item',
      });
    } else {
      items.push(
        {
          label: t('login.title'),
          onClick: () => setShowModalLogin(true),
          type: 'item',
        },
        { label: t('newMemorial.registerMemorial'), to: '/home/memorials/new', type: 'item' },
        {
          label: t('common.changeLanguage'),
          onClick: () => setShowDrawerChangeLanguageMobile(true),
          type: 'item',
        }
      );
    }

    return items.map(({ label, type, to, onClick, disabled }, index) => ({
      label: (
        <p
          className={
            (to?.includes('/home/start') && pathname.includes('/home/start')) ||
            (to?.includes('/home/memorials/list') && pathname.includes('/home/memorials/list')) ||
            pathname === to
              ? 'text-primary'
              : ''
          }
          onClick={disabled ? undefined : to ? () => navigate(to) : onClick ? onClick : undefined}
        >
          {label}
        </p>
      ),
      key: index,
      type,
      disabled,
    }));
  };

  return (
    <div className="flex min-h-[39px] items-center justify-between bg-white px-[16px] md:px-0">
      <div className="relative w-full md:w-[initial]">
        {showDrawerChangeLanguageMobile && (
          <DrawerChangeLanguageMobile onClose={() => setShowDrawerChangeLanguageMobile(false)} />
        )}
        {(showModalLogin || showLoginModalAutomatically) && (
          <Login
            showOnlyModal
            showModal
            onCloseModal={() => {
              setShowModalLogin(false);
              setShowLoginModalAutomatically?.(false);
            }}
          />
        )}
        <div className="flex w-full items-center justify-between">
          <Dropdown
            menu={{ items: getItemsToDropdown() }}
            trigger={['click']}
            placement="bottomRight"
            overlayClassName={`dropdown-menu ${isMobile ? 'menu-mobile' : ''}`}
            overlayStyle={{ minWidth: '240px' }}
          >
            {isMobile ? (
              <Burguer />
            ) : (
              <div className="cursor-pointer">
                <AvatarMemorial
                  image={image}
                  initials={firstName ? `${firstName[0]}${lastName[0]}` : ''}
                  size={48}
                />
              </div>
            )}
          </Dropdown>
          {isMobile && (
            <>
              <Logo className="h-[32px]" onClick={goToWPHome} />
              <div className="w-[16px]" />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Menu;
